import {
  createRouter,
  createRootRoute,
  createRoute,
  type AnyRoute,
  notFound,
} from "@tanstack/react-router";

import { RootLayout } from "./routes/__root";
import { LocaleLayout } from "./routes/locale-layout";
import { LandingPage } from "./pages/LandingPage";
import { FitPage } from "./pages/FitPage";
import { WorkPage } from "./pages/WorkPage";
import { WorkEquipPage } from "./pages/WorkEquipPage";
import { WorkEquipScoutPage } from "./pages/WorkEquipScoutPage";
import { WorkDubcircusPage } from "./pages/WorkDubcircusPage";
import { AboutPage } from "./pages/AboutPage";
import { ProcessPage } from "./pages/ProcessPage";
import { PrivacyPolicyPage } from "./pages/PrivacyPolicyPage";
import { TermsOfServicePage } from "./pages/TermsOfServicePage";
import { ContactPage } from "./pages/ContactPage";
import { DesignAssetsPage } from "./pages/DesignAssetsPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { routeSeo } from "./lib/seo";
import { isLocale, type SupportedLocale } from "@translations/config";
import i18n from "@/lib/i18n";

// Dev-only routes are excluded from the route tree in production builds.
// Bun's bundler inlines `process.env.NODE_ENV` at build time so the conditional
// is statically analyzable and the dev-only branch can be tree-shaken.
const isDev = process.env.NODE_ENV !== "production";

const rootRoute = createRootRoute({
  component: RootLayout,
});

/**
 * Build the public page routes (marketing + legal) under a given
 * parent route, parameterised by locale.
 *
 * Called twice:
 *  - once for English routes mounted directly under rootRoute (URLs like `/work`)
 *  - once for the `/$lang` layout (URLs like `/fr/work`)
 *
 * _design stays English-only and is added separately.
 */
function buildPageRoutes(parent: AnyRoute, locale: SupportedLocale): AnyRoute[] {
  // Locale-pinned translator. `head()` is invoked when a route matches; on the
  // initial load it can run before RootLayout's useSyncI18nLocale effect has
  // flipped i18n.language, so we read from a fixed-locale getter to guarantee
  // the SEO meta matches the URL's locale.
  const t = i18n.getFixedT(locale);

  const indexRoute = createRoute({
    getParentRoute: () => parent,
    path: "/",
    component: LandingPage,
    head: () =>
      routeSeo({
        path: "/",
        locale,
        title: t("seo.home.title"),
        description: t("seo.home.description"),
      }),
  });

  const fitRoute = createRoute({
    getParentRoute: () => parent,
    path: "fit",
    component: FitPage,
    head: () =>
      routeSeo({
        path: "/fit",
        locale,
        title: t("seo.fit.title"),
        description: t("seo.fit.description"),
      }),
  });

  const workRoute = createRoute({
    getParentRoute: () => parent,
    path: "work",
    component: WorkPage,
    head: () =>
      routeSeo({
        path: "/work",
        locale,
        title: t("seo.work.title"),
        description: t("seo.work.description"),
      }),
  });

  const workEquipRoute = createRoute({
    getParentRoute: () => parent,
    path: "work/equip",
    component: WorkEquipPage,
    head: () =>
      routeSeo({
        path: "/work/equip",
        locale,
        title: t("seo.workEquip.title"),
        description: t("seo.workEquip.description"),
      }),
  });

  const workEquipScoutRoute = createRoute({
    getParentRoute: () => parent,
    path: "work/equip-scout",
    component: WorkEquipScoutPage,
    head: () =>
      routeSeo({
        path: "/work/equip-scout",
        locale,
        title: t("seo.workEquipScout.title"),
        description: t("seo.workEquipScout.description"),
      }),
  });

  const workDubcircusRoute = createRoute({
    getParentRoute: () => parent,
    path: "work/dubcircus",
    component: WorkDubcircusPage,
    head: () =>
      routeSeo({
        path: "/work/dubcircus",
        locale,
        title: t("seo.workDubcircus.title"),
        description: t("seo.workDubcircus.description"),
      }),
  });

  const aboutRoute = createRoute({
    getParentRoute: () => parent,
    path: "about",
    component: AboutPage,
    head: () =>
      routeSeo({
        path: "/about",
        locale,
        title: t("seo.about.title"),
        description: t("seo.about.description"),
      }),
  });

  const processRoute = createRoute({
    getParentRoute: () => parent,
    path: "process",
    component: ProcessPage,
    head: () =>
      routeSeo({
        path: "/process",
        locale,
        title: t("seo.process.title"),
        description: t("seo.process.description"),
      }),
  });

  const privacyRoute = createRoute({
    getParentRoute: () => parent,
    path: "privacy",
    component: PrivacyPolicyPage,
    head: () =>
      routeSeo({
        path: "/privacy",
        locale,
        title: t("seo.privacy.title"),
        description: t("seo.privacy.description"),
      }),
  });

  const termsRoute = createRoute({
    getParentRoute: () => parent,
    path: "terms",
    component: TermsOfServicePage,
    head: () =>
      routeSeo({
        path: "/terms",
        locale,
        title: t("seo.terms.title"),
        description: t("seo.terms.description"),
      }),
  });

  const contactRoute = createRoute({
    getParentRoute: () => parent,
    path: "contact",
    component: ContactPage,
    // Only carry ?plan= when it's actually in the URL. Returning a default
    // ({ plan: "" }) makes the validated search differ from the real URL,
    // which the router tries to reconcile with a rewrite — under the SSR
    // pass's memory history that reconciliation never settles and the route
    // renders nothing.
    validateSearch: (search: Record<string, unknown>): { plan?: string } =>
      typeof search.plan === "string" && search.plan !== "" ? { plan: search.plan } : {},
    head: () =>
      routeSeo({
        path: "/contact",
        locale,
        title: t("seo.contact.title"),
        description: t("seo.contact.description"),
      }),
  });

  return [
    indexRoute,
    fitRoute,
    workRoute,
    workEquipRoute,
    workEquipScoutRoute,
    workDubcircusRoute,
    aboutRoute,
    processRoute,
    privacyRoute,
    termsRoute,
    contactRoute,
  ];
}

// English routes mounted directly at the root: /, /fit, /work, ...
const enRoutes = buildPageRoutes(rootRoute, "en");

// SupportedLocale layout for /$lang/... — validates the param against known locales,
// 404s on unknown values, and lets nested routes resolve translations.
const langLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "$lang",
  component: LocaleLayout,
  beforeLoad: ({ params }) => {
    if (!isLocale(params.lang)) {
      throw notFound();
    }
    // English already lives at the root — don't serve duplicate content
    // at /en/... to keep canonical URLs unambiguous.
    if (params.lang === "en") {
      throw notFound();
    }
  },
});

// We need a SupportedLocale value at build time for routeSeo. The only non-default
// locale today is French; if you add more, switch this to a loop over
// SUPPORTED_LOCALES.filter(l => l !== DEFAULT_LOCALE) and build a child tree per locale.
const frRoutes = buildPageRoutes(langLayoutRoute, "fr");
langLayoutRoute.addChildren(frRoutes);

// Internal: design assets (screenshot targets for OG image, LinkedIn banners).
// Dev-only — wrapped in the isDev guard so the route (and the DesignAssetsPage
// reference) tree-shakes out of production builds. /_design 404s in prod.
const designRoute = isDev
  ? createRoute({
      getParentRoute: () => rootRoute,
      path: "/_design",
      component: DesignAssetsPage,
      head: () =>
        routeSeo({
          path: "/_design",
          title: "Design assets · Holy Moly",
          description: "Internal screenshot targets.",
          availableLocales: ["en"],
          noindex: true,
        }),
    })
  : null;

// Build the route tree. Exported so the prerender script
// (scripts/prerender.ts) can build a fresh memory-history router per route ×
// locale pair without colliding with the client-side singleton below.
export const routeTree = rootRoute.addChildren([
  ...enRoutes,
  langLayoutRoute,
  ...(isDev && designRoute ? [designRoute] : []),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  defaultNotFoundComponent: NotFoundPage,
  // Reset scroll to top on new-location navigation (e.g. home -> /about),
  // while still restoring the previous scroll position on back/forward and
  // scrolling to an in-page hash (StatsStrip -> /process#measured,
  // ContactPage -> /#pricing) when there's no cached position for that
  // location yet. See node_modules/@tanstack/router-core/src/scroll-restoration.ts
  // restoreScroll(): cached entry wins, else hash-scroll, else top.
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export default router;
